import { Injectable, inject, signal } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { debounceTime, switchMap, catchError, map } from 'rxjs/operators';
import { toObservable } from '@angular/core/rxjs-interop';
import { AuthService } from './auth.service';
import {
  Product,
  ProductCategory,
  ProductSearchFilters,
} from '../models/product.model';
import type { ActivityTypeInfo } from '../models/cluster.model';
import { LogService } from './log.service';

@Injectable({ providedIn: 'root' })
export class ProductsService {
  private log = inject(LogService);
  private http = inject(HttpClient);
  private authService = inject(AuthService);

  // private catalogApiUrl = 'http://analyticai.com:3000/api/product-catalog';
  // private productsApiUrl = 'http://analyticai.com:3000/api/products';

  private catalogApiUrl = '/api/product-catalog';
  private productsApiUrl = '/api/products';

  /* ==================== AUTH ==================== */
  private getAuthHeaders(): HttpHeaders {
    const token = this.authService.getToken();
    return new HttpHeaders({
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    });
  }

  /* ==================== RXJS SEARCH ==================== */
  private _searchFilters = signal<ProductSearchFilters>({});

  searchResults$: Observable<Product[]> = toObservable(
    this._searchFilters,
  ).pipe(
    debounceTime(300),
    switchMap((filters) => this.searchCatalog(filters)),
    catchError((err) => {
      this.log.error('Erreur recherche catalogue', err);
      return of(); // renvoie Observable vide en cas d'erreur
    }),
  );

  updateSearchFilters(filters: Partial<ProductSearchFilters>) {
    this._searchFilters.update((current) => ({
      ...current,
      ...filters,
    }));
  }

  /* ==================== CATALOG ==================== */

  /** 📋 Catalogue complet */
  getCatalog(): Observable<Product[]> {
    return this.http
      .get<{ success: boolean; data: Product[] }>(this.catalogApiUrl, {
        headers: this.getAuthHeaders(),
      })
      .pipe(map((res) => res.data ?? []));
  }

  /** ⭐ Produits recommandés (par type de marchand) */
  getRecommendedCatalog(): Observable<Product[]> {
    return this.http
      .get<{
        success: boolean;
        data: Product[];
      }>(`${this.catalogApiUrl}/recommended`, {
        headers: this.getAuthHeaders(),
      })
      .pipe(
        map((res) => res.data ?? []), // renvoie un tableau vide si data est null
      );
  }

  /** 🔍 Recherche catalogue */
  private searchCatalog(filters: ProductSearchFilters): Observable<Product[]> {
    let params = new HttpParams();
    if (filters.searchTerm) params = params.set('term', filters.searchTerm);
    if (filters.category && filters.category !== 'all')
      params = params.set('category', filters.category);

    return this.http
      .get<{
        success: boolean;
        data: Product[];
      }>(`${this.catalogApiUrl}/search`, { params })
      .pipe(map((res) => res.data ?? []));
  }

  /* ==================== USER PRODUCTS ==================== */

  /** 💾 Sauvegarde produits sélectionnés par l'utilisateur */
  saveMyProducts(products: Product[]): Observable<Product[]> {
    return this.http
      .post<{
        success: boolean;
        data: Product[];
      }>(
        `${this.productsApiUrl}/import`,
        { products },
        { headers: this.getAuthHeaders() },
      )
      .pipe(map((res) => res.data));
  }

  /** 📂 Récupération des produits utilisateur */
  getMyProducts(): Observable<Product[]> {
    return this.http
      .get<{ success: boolean; data: any[] }>(this.productsApiUrl, {
        headers: this.getAuthHeaders(),
      })
      .pipe(
        map((res) =>
          res.data.map((p) => ({
            ...p,
            id: p._id, // _id de Mongo → id pour Angular
          })),
        ),
      );
  }

  /** ✏️ Mise à jour en masse des produits */
  updateProducts(products: Product[]): Observable<Product[]> {
    return this.http
      .put<{
        success: boolean;
        data: Product[];
      }>(
        `${this.productsApiUrl}/bulk-update`,
        { products },
        { headers: this.getAuthHeaders() },
      )
      .pipe(map((res) => res.data));
  }

  /* ==================== METADATA ==================== */

  private _categories = signal<ProductCategory[]>([]);
  private _units = signal<string[]>([]);
  private _activityTypes = signal<ActivityTypeInfo[]>([]);

  // ✅ Signal accessors (lecture directe, compatible zoneless)
  readonly categories = this._categories.asReadonly();
  readonly units = this._units.asReadonly();
  readonly activityTypes = this._activityTypes.asReadonly();

  // ✅ getters publics (Observable)
  getCategories(): Observable<ProductCategory[]> {
    return toObservable(this._categories);
  }

  getUnits(): Observable<string[]> {
    return toObservable(this._units);
  }

  getActivityTypes(): Observable<ActivityTypeInfo[]> {
    return toObservable(this._activityTypes);
  }

  /** Charger toutes les meta depuis le backend */
  loadMeta(): Observable<void> {
    return this.http
      .get<{
        success: boolean;
        data: {
          categories: ProductCategory[];
          units: string[];
          activityTypes: ActivityTypeInfo[];
        };
      }>(`${this.productsApiUrl}/metadata`, {
        headers: this.getAuthHeaders(),
      })
      .pipe(
        map((res) => {
          const meta = res.data ?? {
            categories: [],
            units: [],
            activityTypes: [],
          };
          this._categories.set(meta.categories);
          this._units.set(meta.units);
          this._activityTypes.set(meta.activityTypes);
        }),
        catchError((err) => {
          this.log.error('Erreur chargement product meta', err);
          // fallback vide
          this._categories.set([]);
          this._units.set([]);
          this._activityTypes.set([]);
          return of();
        }),
      );
  }

  /** Observables pour s'abonner dans les composants */
  getCategories$(): Observable<ProductCategory[]> {
    return toObservable(this._categories);
  }

  getUnits$(): Observable<string[]> {
    return toObservable(this._units);
  }

  getActivityTypes$(): Observable<ActivityTypeInfo[]> {
    return toObservable(this._activityTypes);
  }

  /* ==================== TEMPLATES ==================== */
  getTemplates(params?: {
    merchantType?: string;
    activityType?: string;
    region?: string;
  }): Observable<any> {
    let httpParams = new HttpParams();
    if (params?.merchantType)
      httpParams = httpParams.set('merchantType', params.merchantType);
    if (params?.activityType)
      httpParams = httpParams.set('activityType', params.activityType);
    if (params?.region) httpParams = httpParams.set('region', params.region);
    return this.http.get(`${this.catalogApiUrl}/templates`, {
      headers: this.getAuthHeaders(),
      params: httpParams,
    });
  }

  getTemplateDetail(id: string): Observable<any> {
    return this.http.get(`${this.catalogApiUrl}/templates/${id}`, {
      headers: this.getAuthHeaders(),
    });
  }

  applyTemplate(
    id: string,
    mode: 'replace' | 'merge' = 'merge',
  ): Observable<any> {
    return this.http.post(
      `${this.catalogApiUrl}/templates/${id}/apply`,
      { mode },
      { headers: this.getAuthHeaders() },
    );
  }
}
